import _ from 'underscore';
import React, {
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState
} from 'react';
import PropTypes from 'prop-types';
import { SwitchTransition } from 'react-transition-group';
import cx from 'classnames';
import { getImageUrl } from '@culture/common-helpers/cultureUtils';

import { ImageFormatContext } from 'contexts/ImageFormatContext';
import Animation from 'components/Animation';
import VideoPlayer from 'components/VideoPlayer';
import LazyLoadImage from 'components/LazyLoad/Image';
import { HeaderContext } from 'contexts/HeaderContext';
import Tabs from 'components/Tabs';
import Container from 'components/Container';

import { screenBreakpoints } from 'utils/helpers/constants';
import { getHorizontalFile } from 'utils/helpers/files';

import { ThumbnailFileCropType } from 'utils/propTypes/common';

import styles from './styles.module.scss';

const VideoContainer = ({
	thumbnailFile,
	additionalThumbnailFile,
	additionalThumbnailFileCrop,
	theme,
	children,
	slides,
	onToggleVideo,
	slidesClassName,
	withDisclaimer = false
}) => {
	const ref = useRef(null);
	const playButtonRef = useRef(null);
	const [showVideo, setShowVideo] = useState(null);
	const [smallVideo, setSmallVideo] = useState(false);
	const [imageSrc, setImageSrc] = useState('');
	const [, setHeaderContext] = useContext(HeaderContext);
	const imageFormat = useContext(ImageFormatContext);
	const [activeSlide, setActiveSlide] = useState(
		(slides.length > 1 &&
			_(slides).find(({ video: { subType } }) => subType !== 'trailer')) ||
			_(slides[0]).omit('title')
	);

	const hasCropParams = !_.isEmpty(additionalThumbnailFileCrop);
	const image = hasCropParams
		? additionalThumbnailFile
		: getHorizontalFile(thumbnailFile, additionalThumbnailFile, {});

	useEffect(() => {
		const onResize = () => {
			let params;
			if (hasCropParams) {
				params = additionalThumbnailFileCrop;
			} else {
				const imageSizeSmallScreen = {
					width: 1366,
					height: 441
				};
				const imageSizeLargeScreen = {
					width: 1900,
					height: 782
				};

				params =
					window.innerWidth <= screenBreakpoints.notebook
						? imageSizeSmallScreen
						: imageSizeLargeScreen;
			}

			setImageSrc(
				getImageUrl(image, {
					format: imageFormat,
					params: { crop: 'crop', gravity: 'center', ...params }
				})
			);
		};

		onResize();

		window.addEventListener('resize', onResize);

		return () => window.removeEventListener('resize', onResize);
	}, []);

	useEffect(() => {
		const headerModifiers = showVideo ? { theme: 'dark' } : {};
		setHeaderContext(headerModifiers);

		return () => {
			if (showVideo) {
				setHeaderContext({});
			}
		};
	}, [showVideo]);

	useEffect(() => {
		let cleanup = _.noop;

		if (showVideo) {
			const onScroll = () => {
				const { height, top } = ref.current.getBoundingClientRect();

				setSmallVideo(-top > height);
			};

			window.addEventListener('scroll', onScroll);

			cleanup = () => window.removeEventListener('scroll', onScroll);
		}

		return cleanup;
	}, [showVideo]);

	const toggleShowVideo = useCallback(() => {
		setShowVideo(!showVideo);

		if (showVideo) {
			setTimeout(() => {
				playButtonRef.current?.focus();
			}, 0);
			setSmallVideo(false);
		}

		onToggleVideo?.(!showVideo);
	}, [showVideo, activeSlide]);

	const videoContainerCx = cx(styles.videoContainer, styles[`theme_${theme}`], {
		[styles.videoContainer_withDisclaimer]: withDisclaimer
	});

	const videoCx = cx(styles.video, {
		[styles.small]: smallVideo
	});

	return (
		<div className={videoContainerCx}>
			<div className={cx(styles.inner, 'pvi-no-styles')} ref={ref}>
				<LazyLoadImage
					key={imageSrc}
					className={styles.imageWrapper}
					imageSrc={imageSrc}
				>
					<div
						className={styles.image}
						style={{ backgroundImage: `url("${imageSrc}")` }}
					/>
				</LazyLoadImage>
				<SwitchTransition mode="in-out">
					<Animation
						show
						key={showVideo}
						addEndListener={(node, done) =>
							node.addEventListener('transitionend', done, false)
						}
						timeout={100}
						animation="fade"
					>
						{showVideo ? (
							<div className={videoCx}>
								<VideoPlayer
									{...activeSlide.video}
									theme={smallVideo ? 'small' : null}
									onCloseButtonClick={toggleShowVideo}
									markupAttributes={{ closeButton: { 'data-player': 'stop' } }}
								/>
							</div>
						) : (
							<div className={cx(styles.content, 'pvi-no-styles')}>
								{children(toggleShowVideo, activeSlide, playButtonRef)}
							</div>
						)}
					</Animation>
				</SwitchTransition>
			</div>
			{slides.length > 1 && (
				<Container className={cx(styles.slides, slidesClassName)}>
					<Tabs
						tabs={slides}
						onClick={setActiveSlide}
						activeTab={activeSlide}
					/>
				</Container>
			)}
		</div>
	);
};

VideoContainer.propTypes = {
	slides: PropTypes.array.isRequired,
	children: PropTypes.func.isRequired,
	theme: PropTypes.oneOf(['entity']),
	thumbnailFile: PropTypes.object,
	additionalThumbnailFile: PropTypes.object,
	additionalThumbnailFileCrop: ThumbnailFileCropType,
	materials: PropTypes.array.isRequired,
	onToggleVideo: PropTypes.func,
	slidesClassName: PropTypes.string,
	withDisclaimer: PropTypes.bool
};

export default VideoContainer;
